import React, { useMemo, useState, useEffect } from 'react'
import { DesktopNavigation } from './DesktopNavigation'
import { MobileNavigation } from './MobileNavigation'
import { useUser } from '../../contextManager/user.context'
import { useUnreadMessages } from '../../hooks/useUnreadMessages'
import { getTutorProfileStatus, getParentProfileStatus } from '../../utils'
import { getInitials } from '../../utils/name'
import { ProfileStatusBadge } from './ProfileStatusBadge'

// icons
import {
  PersonFill,
  ChatDotsFill,
  JournalBookmarkFill,
  CalendarFill,
  Search,
  ShieldLockFill
} from 'react-bootstrap-icons'

const useIsWideScreen = (breakpoint = 600) => {
  const [isWide, setIsWide] = useState(window.innerWidth > breakpoint)

  useEffect(() => {
    const handleResize = () => setIsWide(window.innerWidth > breakpoint)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [breakpoint])

  return isWide
}

export const NavigationSwitcher: React.FC = () => {
  const { userDetails } = useUser()
  const isWideScreen = useIsWideScreen(720)

  const ICON_SIZE = 30
  const { unreadCount, displayCount } = useUnreadMessages()

  const navLinks = useMemo(() => {
    // --- ProfileIcon ---
    // Shows photo (or PersonFill) + a status badge:
    //   🟠 "!" → incomplete
    //   🟠 "✓" → complete but not verified
    //   🟢 "✓" → complete and verified
    const ProfileIcon: React.FC<{ size?: string | number }> = ({ size }) => {
      const isTutor = userDetails?.role === 'tutor'
      const { isComplete } = isTutor
        ? getTutorProfileStatus(userDetails)
        : getParentProfileStatus(userDetails)
      const isVerified = !!userDetails?.account_verified

      // Determine badge props
      let badgeSymbol: '✓' | '!' | null = null
      let badgeColor = '#f97316' // orange default

      if (!isTutor) {
        // Parents are verified as soon as their profile is complete
        badgeSymbol = isComplete ? '✓' : '!'
        badgeColor = isComplete ? '#22c55e' : '#f97316'
      } else if (isComplete && isVerified) {
        badgeSymbol = '✓'
        badgeColor = '#22c55e' // green
      } else if (isComplete && !isVerified) {
        badgeSymbol = '✓'
        badgeColor = '#f97316' // orange
      } else if (!isComplete) {
        badgeSymbol = '!'
        badgeColor = '#f97316' // orange
      }

      return (
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          {userDetails?.profilePhotoUrl ? (
            <img
              src={userDetails.profilePhotoUrl}
              alt="Your avatar"
              style={{
                width: ICON_SIZE,
                height: ICON_SIZE,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #4CAF50',
                boxSizing: 'border-box',
              }}
            />
          ) : userDetails?.name ? (
            <span
              aria-label="Your avatar"
              style={{
                width: ICON_SIZE,
                height: ICON_SIZE,
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#d1fae5',
                border: '2px solid #4CAF50',
                color: '#065f46',
                fontSize: ICON_SIZE * 0.4,
                fontWeight: 700,
                boxSizing: 'border-box',
                lineHeight: 1,
              }}
            >
              {getInitials(userDetails.name)}
            </span>
          ) : (
            <PersonFill size={size || ICON_SIZE} />
          )}

          {badgeSymbol && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                bottom: 0,
                right: -3,
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: badgeColor,
                border: '2px solid white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 8,
                color: 'white',
                fontWeight: 700,
                lineHeight: 1,
                pointerEvents: 'none',
              }}
            >
              {badgeSymbol}
            </span>
          )}
        </span>
      )
    }

    // --- MessengerIcon ---
    const MessengerIcon: React.FC<{ size?: string | number }> = ({ size }) => (
      <span
        style={{
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
          width: '100%',
          height: '100%',
          lineHeight: 0,
        }}
      >
        <ChatDotsFill size={size || ICON_SIZE} />

        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: isWideScreen ? 2 : 7,
              right: isWideScreen ? 2 : 7,
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              borderRadius: 8,
              background: '#E53935',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: '16px',
              textAlign: 'center',
              boxShadow: '0 0 0 2px white',
              pointerEvents: 'none',
              transform: 'scale(0.8)',
            }}
          >
            {displayCount}
          </span>
        )}
      </span>
    )

    const LessonsIcon  = ({ size }: { size?: string | number }) => <JournalBookmarkFill size={size || ICON_SIZE} />
    const TimelineIcon = ({ size }: { size?: string | number }) => <CalendarFill        size={size || ICON_SIZE} />
    const SearchIcon   = ({ size }: { size?: string | number }) => <Search              size={size || ICON_SIZE} />

    const messengerTitle = unreadCount > 0 ? `Messenger (${displayCount})` : 'Messenger'
    const messengerAria  = unreadCount > 0 ? `Messenger, ${displayCount} unread conversations` : 'Messenger'

    const commonLinks = [
      {
        to: '/profile',
        title: 'Profile',
        'aria-label': 'Profile',
        icon: ProfileIcon,
      },
      {
        to: '/search',
        title: 'Search',
        'aria-label': 'Search',
        icon: SearchIcon,
      },
      {
        to: '/messenger',
        title: messengerTitle,
        'aria-label': messengerAria,
        icon: MessengerIcon,
      },
      {
        to: '/lessons',
        title: 'Lessons',
        'aria-label': 'Lessons',
        icon: LessonsIcon,
      },
      {
        to: '/strippedTimeline',
        title: 'Timeline',
        'aria-label': 'Timeline',
        icon: TimelineIcon,
      },
    ]

    const AdminIcon = ({ size }: { size?: string | number }) => <ShieldLockFill size={size || ICON_SIZE} />

    if (userDetails?.role === 'tutor' || userDetails?.role === 'parent') {
      if (userDetails?.isAdmin) {
        return [
          ...commonLinks,
          {
            to: '/admin',
            title: 'Admin',
            'aria-label': 'Admin tutor review',
            icon: AdminIcon,
          },
        ]
      }
      return commonLinks
    }

    return []
  }, [userDetails, unreadCount, displayCount, ICON_SIZE, isWideScreen])

  return isWideScreen
    ? <DesktopNavigation reactRouterLinks={navLinks} />
    : <MobileNavigation reactRouterLinks={navLinks} />
}